<script setup lang="ts">
    import { computed, onMounted, shallowReactive, watch, type Ref, ref } from 'vue'
    import { useRoute } from 'vue-router'

    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import ScenarioNavigation from '@Quest/CorporateScenarios/Components/ScenarioNavigation.vue'
    import SelectorDialog from '@Quest/PortfolioScenarios/Components/SelectorDialog.vue'
    import NumericInputDialog from '@common/Components/NumericInputDialog.vue'

    import { NumericInputDialogViewModel } from '@common/Components/NumericInputDialog'
    import { SelectorDialogViewModel } from '@Quest/PortfolioScenarios/Components/SelectorDialog'

    import
    {
        calculateProportionOf,
        parseOptionalNumber,
        sumOrDefault,
        toPercentageString,
        toStringSmartly
    } from '@common/Common'

    import type {
        BudgetAllocationSummaryReadModel,
        PortfolioBudgetAllocationReadModel,
        PortfolioResourceAllocationReadModel,
        PortfoliosPageReadModel,
        PortfolioSummaryReadModel,
        ResourceAllocationSummaryReadModel
    } from '@Quest/CorporateScenarios/readmodels'

    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))

    // ======================
    // Query
    // ======================

    import { usePortfoliosPageQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'

    const {
        runPortfoliosPageQueryV1,
        modelPortfoliosPageQueryV1
    } = usePortfoliosPageQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelPortfoliosPageQueryV1 as Ref<PortfoliosPageReadModel | null>

    // ======================
    // Commands
    // ======================

    import
        {
            useSetPortfolioResourceAllocationCommandV1,
            useUpdateActivePortfolioScenarioCommandV1,
            useSetPortfolioTotalCostLimitCommandV1,
            useSetPortfolioCapexLimitCommandV1,
            useSetPortfolioOpexLimitCommandV1,
            useSetPortfolioOthexLimitCommandV1,
            useSetPortfolioExpensesLimitCommandV1,
            useSetPortfolioOpexExpensesLimitCommandV1
        } from '@Quest/CorporateScenarios/commands-vue-composables'

    import { SetPortfolioResourceAllocationCommandV1 } from '../commands-core'

    const {
        invokeSetPortfolioResourceAllocationCommandV1,
        onSucceedSetPortfolioResourceAllocationCommandV1,
        command: setPortfolioResourceAllocationCommand
    } = useSetPortfolioResourceAllocationCommandV1(workspaceId, strategyId, scenarioId)

    const {
        invokeUpdateActivePortfolioScenarioCommandV1,
        onSucceedUpdateActivePortfolioScenarioCommandV1
    } = useUpdateActivePortfolioScenarioCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioTotalCostLimitCommandV1 } =
        useSetPortfolioTotalCostLimitCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioCapexLimitCommandV1 } =
        useSetPortfolioCapexLimitCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioOpexLimitCommandV1 } =
        useSetPortfolioOpexLimitCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioOthexLimitCommandV1 } =
        useSetPortfolioOthexLimitCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioExpensesLimitCommandV1 } =
        useSetPortfolioExpensesLimitCommandV1(workspaceId, strategyId, scenarioId)

    const { invokeSetPortfolioOpexExpensesLimitCommandV1 } =
        useSetPortfolioOpexExpensesLimitCommandV1(workspaceId, strategyId, scenarioId)

    // ======================
    // ViewModels + state (1:1)
    // ======================

    const selectorDialog = shallowReactive(new SelectorDialogViewModel())

    const totalCostLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const opexLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const othexLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const capexLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const expensesLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const opexExpensesLimitEditorDialog = shallowReactive(new NumericInputDialogViewModel())
    const portfolioResourceAllocationEditorDialog = shallowReactive(new NumericInputDialogViewModel())

    const budget = ref<BudgetAllocationSummaryViewModel | null>(null)
    const resources = ref<ResourceAllocationSummaryViewModel[]>([])

    // replace onSetModel
    watch(rm, (current) =>
    {
        if (!current) return

        resources.value = current.Resources.map(r => new ResourceAllocationSummaryViewModel(r))
        budget.value = new BudgetAllocationSummaryViewModel(current.Budget)
    })

    onSucceedUpdateActivePortfolioScenarioCommandV1(() =>
    {
        window.location.reload()
    })

    onSucceedSetPortfolioResourceAllocationCommandV1(() =>
    {
        const cmd1 = setPortfolioResourceAllocationCommand.value as SetPortfolioResourceAllocationCommandV1

        const resource = resources.value.find(r => r.Id === cmd1.ResourceId)

        if (resource)
        {
            resource.setPortfolioAllocation(cmd1.PortfolioId, cmd1.Allocation)
        }
    })

    // ======================
    // Formatting helpers (1:1)
    // ======================

    function fmtNum(val: number | null) { return toStringSmartly(val) }
    function fmtPct(val: number | null, decimalPlaces: number = 1) { return toPercentageString(val, decimalPlaces) }

    // ======================
    // UI actions (1:1)
    // ======================

    function showSelectorDialog(p: PortfolioSummaryReadModel)
    {
        selectorDialog.show(p.WorkspaceId, p.PortfolioId, p.ScenarioId)
    }

    function showTotalCostLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        totalCostLimitEditorDialog.id = p.Id
        totalCostLimitEditorDialog.value = p.Limit

        totalCostLimitEditorDialog.title = p.Name
        totalCostLimitEditorDialog.label = `Total cost limit (${rm.value?.CurrencyLabel}):`

        totalCostLimitEditorDialog.showValueRange = false
        totalCostLimitEditorDialog.minValue = 0
        totalCostLimitEditorDialog.show()
    }

    function showOpexLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        opexLimitEditorDialog.id = p.Id
        opexLimitEditorDialog.value = p.Limit

        opexLimitEditorDialog.title = p.Name
        opexLimitEditorDialog.label = `Opex limit (${rm.value?.CurrencyLabel}):`

        opexLimitEditorDialog.showValueRange = false
        opexLimitEditorDialog.minValue = 0
        opexLimitEditorDialog.show()
    }

    function showOthexLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        othexLimitEditorDialog.id = p.Id
        othexLimitEditorDialog.value = p.Limit

        othexLimitEditorDialog.title = p.Name
        othexLimitEditorDialog.label = `Other expenses limit (${rm.value?.CurrencyLabel}):`

        othexLimitEditorDialog.showValueRange = false
        othexLimitEditorDialog.minValue = 0
        othexLimitEditorDialog.show()
    }

    function showExpensesLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        expensesLimitEditorDialog.id = p.Id
        expensesLimitEditorDialog.value = p.Limit

        expensesLimitEditorDialog.title = p.Name
        expensesLimitEditorDialog.label = `Expenses limit (${rm.value?.CurrencyLabel}):`

        expensesLimitEditorDialog.showValueRange = false
        expensesLimitEditorDialog.minValue = 0
        expensesLimitEditorDialog.show()
    }

    function showOpexExpensesLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        opexExpensesLimitEditorDialog.id = p.Id
        opexExpensesLimitEditorDialog.value = p.Limit

        opexExpensesLimitEditorDialog.title = p.Name
        opexExpensesLimitEditorDialog.label = `Opex expenses limit (${rm.value?.CurrencyLabel}):`

        opexExpensesLimitEditorDialog.showValueRange = false
        opexExpensesLimitEditorDialog.minValue = 0
        opexExpensesLimitEditorDialog.show()
    }

    function showCapexLimitEditor(p: PortfolioBudgetAllocationViewModel)
    {
        capexLimitEditorDialog.id = p.Id
        capexLimitEditorDialog.value = p.Limit

        capexLimitEditorDialog.title = p.Name
        capexLimitEditorDialog.label = `Capex limit (${rm.value?.CurrencyLabel}):`

        capexLimitEditorDialog.showValueRange = false
        capexLimitEditorDialog.minValue = 0
        capexLimitEditorDialog.show()
    }

    function showPortfolioResourceAllocationEditorDialog(p: PortfolioResourceAllocationViewModel)
    {
        portfolioResourceAllocationEditorDialog.id = p.Id
        portfolioResourceAllocationEditorDialog.value = p.TargetPercent != null ? 100 * p.TargetPercent : null

        portfolioResourceAllocationEditorDialog.title = p.Name
        portfolioResourceAllocationEditorDialog.label = `Target allocation (%):`

        portfolioResourceAllocationEditorDialog.showValueRange = true
        portfolioResourceAllocationEditorDialog.minValue = 0
        portfolioResourceAllocationEditorDialog.maxValue = 200
        portfolioResourceAllocationEditorDialog.step = 1
        portfolioResourceAllocationEditorDialog.opener = p
        portfolioResourceAllocationEditorDialog.show()
    }

    // ======================
    // Template event handlers (RESTORED 1:1)
    // ======================

    function handlePortfolioResourceAllocationEditorUpdate(value: string | null)
    {
        const allocation = parseOptionalNumber(value)
        const p = portfolioResourceAllocationEditorDialog.opener as PortfolioResourceAllocationViewModel

        invokeSetPortfolioResourceAllocationCommandV1({
            PortfolioId: p.Id,
            ResourceId: p.root.Id,
            Allocation: allocation != null ? allocation / 100 : null
        })

        portfolioResourceAllocationEditorDialog.close()
    }

    function handleUpdateActivePortfolioScenarioCommand(portfolioId: string, portfolioScenarioId: string)
    {
        invokeUpdateActivePortfolioScenarioCommandV1({
            PortfolioId: portfolioId,
            PortfolioScenarioId: portfolioScenarioId
        })
    }

    function handleSetPortfolioTotalCostLimitCommand(value: string | null, portfolioId: string): void
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioTotalCostLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateTotalCostLimit(portfolioId, limit)
        totalCostLimitEditorDialog.close()
    }

    function handleSetPortfolioCapexLimitCommand(value: string | null, portfolioId: string): void
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioCapexLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateCapexLimit(portfolioId, limit)
        capexLimitEditorDialog.close()
    }

    function handleSetPortfolioOpexLimitCommand(value: string | null, portfolioId: string)
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioOpexLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateOpexLimit(portfolioId, limit)
        opexLimitEditorDialog.close()
    }

    function handleSetPortfolioOthexLimitCommand(value: string | null, portfolioId: string)
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioOthexLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateOthexLimit(portfolioId, limit)
        othexLimitEditorDialog.close()
    }

    function handleSetPortfolioExpensesLimitCommand(value: string | null, portfolioId: string)
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioExpensesLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateExpensesLimit(portfolioId, limit)
        expensesLimitEditorDialog.close()
    }

    function handleSetPortfolioOpexExpensesLimitCommand(value: string | null, portfolioId: string)
    {
        const limit = parseOptionalNumber(value)

        invokeSetPortfolioOpexExpensesLimitCommandV1({
            PortfolioId: portfolioId,
            Limit: limit
        })

        budget.value?.updateOpexExpensesLimit(portfolioId, limit)
        opexExpensesLimitEditorDialog.close()
    }

    function onSelected(_result: any)
    {
        runPortfoliosPageQueryV1()
    }

    function onScheduled(_result: any)
    {
        runPortfoliosPageQueryV1()
    }

    // ======================
    // Lifecycle (1:1)
    // ======================

    onMounted(() =>
    {
        document.title = "Enterprise Plan - Portfolios"
        runPortfoliosPageQueryV1()
    })

    // ======================
    // ViewModel classes (UNCHANGED)
    // ======================

    class ResourceAllocationSummaryViewModel
    {
        Id: string
        Name: string
        Unit: string
        IsCollapsed: boolean
        CapacityValue: number
        Portfolios: PortfolioResourceAllocationViewModel[]

        get TargetPercent(): number | null { return sumOrDefault(this.Portfolios.map(a => a.TargetPercent)) }
        get TargetValue(): number | null { return sumOrDefault(this.Portfolios.map(a => a.TargetValue)) }
        get CurrentPercent(): number | null { return calculateProportionOf(this.CurrentValue, this.TargetValue) }
        get CurrentValue(): number | null { return sumOrDefault(this.Portfolios.map(a => a.CurrentValue)) }

        constructor(rm: ResourceAllocationSummaryReadModel)
        {
            this.Id = rm.Id
            this.Name = rm.Name
            this.Unit = rm.Unit
            this.IsCollapsed = rm.IsCollapsed
            this.CapacityValue = rm.CapacityValue
            this.Portfolios = rm.Portfolios.map(p => new PortfolioResourceAllocationViewModel(this, p))
        }

        setPortfolioAllocation(portfolioId: string, allocation: number | null)
        {
            const portfolio = this.Portfolios.find(p => p.Id === portfolioId)
            if (portfolio)
            {
                portfolio.TargetPercent = allocation
            }
        }
    }

    class PortfolioResourceAllocationViewModel
    {
        root: ResourceAllocationSummaryViewModel

        Id: string
        Name: string
        IsLinked: boolean

        TargetPercent: number | null
        CurrentValue: number | null

        constructor(resource: ResourceAllocationSummaryViewModel, rm: PortfolioResourceAllocationReadModel)
        {
            this.root = resource

            this.Id = rm.Id
            this.Name = rm.Name
            this.IsLinked = rm.IsLinked
            this.TargetPercent = rm.TargetPercent
            this.CurrentValue = rm.CurrentValue
        }

        get TargetValue(): number | null
        {
            return this.TargetPercent === null ? null : this.TargetPercent * this.root.CapacityValue
        }

        get CurrentPercent(): number | null { return calculateProportionOf(this.CurrentValue, this.TargetValue) }
    }

    class BudgetAllocationSummaryViewModel
    {
        TotalCostItems: PortfolioBudgetAllocationViewModel[]
        CapexItems: PortfolioBudgetAllocationViewModel[]
        OpexItems: PortfolioBudgetAllocationViewModel[]
        OthexItems: PortfolioBudgetAllocationViewModel[]
        ExpensesItems: PortfolioBudgetAllocationViewModel[]
        OpexExpensesItems: PortfolioBudgetAllocationViewModel[]

        TotalCostCollapsed: boolean = true
        CapexCollapsed: boolean = true
        OpexCollapsed: boolean = true
        OthexCollapsed: boolean = true
        ExpensesCollapsed: boolean = true
        OpexExpensesCollapsed: boolean = true

        get TotalCostLimit(): number | null { return sumOrDefault(this.TotalCostItems.map(a => a.Limit)) }
        get TotalCostCurrentValue(): number | null { return sumOrDefault(this.TotalCostItems.map(a => a.CurrentValue)) }
        get CapexLimit(): number | null { return sumOrDefault(this.CapexItems.map(a => a.Limit)) }
        get CapexCurrentValue(): number | null { return sumOrDefault(this.CapexItems.map(a => a.CurrentValue)) }
        get OpexLimit(): number | null { return sumOrDefault(this.OpexItems.map(a => a.Limit)) }
        get OpexCurrentValue(): number | null { return sumOrDefault(this.OpexItems.map(a => a.CurrentValue)) }
        get OthexLimit(): number | null { return sumOrDefault(this.OthexItems.map(a => a.Limit)) }
        get OthexCurrentValue(): number | null { return sumOrDefault(this.OthexItems.map(a => a.CurrentValue)) }
        get ExpensesLimit(): number | null { return sumOrDefault(this.ExpensesItems.map(a => a.Limit)) }
        get ExpensesCurrentValue(): number | null { return sumOrDefault(this.ExpensesItems.map(a => a.CurrentValue)) }
        get OpexExpensesLimit(): number | null { return sumOrDefault(this.OpexExpensesItems.map(a => a.Limit)) }
        get OpexExpensesCurrentValue(): number | null { return sumOrDefault(this.OpexExpensesItems.map(a => a.CurrentValue)) }

        constructor(rm: BudgetAllocationSummaryReadModel)
        {
            this.TotalCostItems = rm.TotalCost.map(a => new PortfolioBudgetAllocationViewModel(this, a))
            this.CapexItems = rm.Capex.map(a => new PortfolioBudgetAllocationViewModel(this, a))
            this.OpexItems = rm.Opex.map(a => new PortfolioBudgetAllocationViewModel(this, a))
            this.OthexItems = rm.Othex.map(a => new PortfolioBudgetAllocationViewModel(this, a))
            this.ExpensesItems = rm.Expenses.map(a => new PortfolioBudgetAllocationViewModel(this, a))
            this.OpexExpensesItems = rm.OpexExpenses.map(a => new PortfolioBudgetAllocationViewModel(this, a))
        }

        updateTotalCostLimit(portfolioId: string, value: number | null)
        {
            const item = this.TotalCostItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }

        updateOpexLimit(portfolioId: string, value: number | null)
        {
            const item = this.OpexItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }

        updateOthexLimit(portfolioId: string, value: number | null)
        {
            const item = this.OthexItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }

        updateExpensesLimit(portfolioId: string, value: number | null)
        {
            const item = this.ExpensesItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }

        updateOpexExpensesLimit(portfolioId: string, value: number | null)
        {
            const item = this.OpexExpensesItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }

        updateCapexLimit(portfolioId: string, value: number | null)
        {
            const item = this.CapexItems.find(p => p.Id === portfolioId)
            if (item) item.Limit = value
        }
    }

    class PortfolioBudgetAllocationViewModel
    {
        root: BudgetAllocationSummaryViewModel

        Id: string
        Name: string
        Limit: number | null
        CurrentValue: number | null

        constructor(root: BudgetAllocationSummaryViewModel, rm: PortfolioBudgetAllocationReadModel)
        {
            this.root = root

            this.Id = rm.Id
            this.Name = rm.Name
            this.Limit = rm.Limit
            this.CurrentValue = rm.CurrentValue
        }
    }
</script>

<template>
    <div>
        <SelectorDialog :viewModel="selectorDialog" @selected="onSelected" />

        <NumericInputDialog :viewModel="totalCostLimitEditorDialog" @update-value="handleSetPortfolioTotalCostLimitCommand" />
        <NumericInputDialog :viewModel="opexLimitEditorDialog" @update-value="handleSetPortfolioOpexLimitCommand" />
        <NumericInputDialog :viewModel="othexLimitEditorDialog" @update-value="handleSetPortfolioOthexLimitCommand" />
        <NumericInputDialog :viewModel="capexLimitEditorDialog" @update-value="handleSetPortfolioCapexLimitCommand" />
        <NumericInputDialog :viewModel="expensesLimitEditorDialog" @update-value="handleSetPortfolioExpensesLimitCommand" />
        <NumericInputDialog :viewModel="opexExpensesLimitEditorDialog" @update-value="handleSetPortfolioOpexExpensesLimitCommand" />
        <NumericInputDialog :viewModel="portfolioResourceAllocationEditorDialog" @update-value="handlePortfolioResourceAllocationEditorUpdate" />

        <CorporateNavigation ActivePage="dashboard" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">Enterprise Plan: {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Portfolios</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <ScenarioNavigation @scheduled="onScheduled" />
                </div>
            </div>

            <hr />

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead class="table-secondary">
                        <tr>
                            <th colspan="2" class="align-middle">Portfolio</th>
                            <th class="align-middle text-center">Requested projects</th>
                            <th class="align-middle text-center">Selected projects</th>
                            <th class="align-middle text-center">Value points</th>
                            <th class="align-middle text-center">Revenue <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center" :title="rm.TotalCostLabel">Cost <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Cost of value point<br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Strategic fit <br /> (0-100)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in rm.Portfolios" :key="p.PortfolioId">

                            <td>
                                {{p.PortfolioName}}<br />
                                <template v-if="p.ScenarioId">
                                    <a class="text-primary" v-sanitizedHref="`/#/portfolio-plan/projects/${p.WorkspaceId}/${p.TchProjectId}/${p.ScenarioId}`">
                                        {{p.ScenarioName}}
                                    </a>
                                </template>
                                <template v-if="!p.ScenarioId">
                                    <a class="text-danger" v-sanitizedHref="`/#/portfolio/plans/${p.WorkspaceId}/${p.TchProjectId}`">
                                        No portfolio plan
                                    </a>
                                </template>
                            </td>
                            <td style="width: 120px;" class="text-center align-middle">
                                <div class="dropdown d-inline mr-1">
                                    <button type="button" class="btn btn-outline-secondary" data-toggle="dropdown">
                                        <i class="fa-solid fa-ellipsis"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <h6 class="dropdown-header">Portfolio plans</h6>
                                        <a class="dropdown-item" v-for="s in p.Scenarios" :key="s.Id" @click="handleUpdateActivePortfolioScenarioCommand(p.PortfolioId, s.Id)">{{s.Name}}</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" v-sanitizedHref="`/#/portfolio/plans/${p.WorkspaceId}/${p.TchProjectId}`">Manage plans</a>
                                    </div>
                                </div>
                                <button class="btn btn-success" @click="showSelectorDialog(p)" :disabled="p.ScenarioId == null" title="Selector">
                                    <i class="fa-solid fa-robot"></i>
                                </button>
                            </td>

                            <td class="text-center">
                                {{p.RequestedProjectsCount}}<br />
                                <small class="text-muted">{{fmtPct(p.RequestedProjectsCountShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{p.AcceptedProjectsCount}}<br />
                                <small class="text-muted">{{fmtPct(p.AcceptedProjectsCountShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedValuePoints)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedValuePointsShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedRevenue)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedRevenueShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedCost)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedCostShare)}}</small>
                            </td>
                            <td class="text-center align-middle">
                                {{fmtNum(p.CostOfValuePoint)}}
                            </td>
                            <td class="text-center align-middle">
                                {{fmtNum(p.StrategicFit, 10)}}
                            </td>

                        </tr>

                    </tbody>
                    <tfoot class="">
                        <tr>
                            <td colspan="2"></td>
                            <th class="text-center">
                                {{rm.RequestedProjectsCount}}
                            </th>
                            <th class="text-center">
                                {{rm.AcceptedProjectsCount}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedValuePoints)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedRevenue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedCost)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.CostOfValuePoint)}}
                            </th>
                            <td class="text-center">
                            </td>

                        </tr>
                    </tfoot>

                </table>
            </div>

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th colspan="2" class="align-middle">Budget allocation</th>
                            <th class="align-middle text-center">Allocation <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Limit<br /> ({{rm.CurrencyLabel}})</th>
                        </tr>
                    </thead>
                    <tbody class="totalCost">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.TotalCostCollapsed = !budget.TotalCostCollapsed">
                                    <i :class="['fa', budget.TotalCostCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Total cost<br />
                                <small class="text-muted">{{rm.TotalCostLabel}}</small>
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.TotalCostCurrentValue)}}
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.TotalCostLimit)}}
                            </th>

                        </tr>
                        <tr v-show="budget.TotalCostCollapsed === false" v-for="p in budget.TotalCostItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>

                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>

                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showTotalCostLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showTotalCostLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="opex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OpexCollapsed = !budget.OpexCollapsed">
                                    <i :class="['fa', budget.OpexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Opex
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.OpexCurrentValue)}}
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.OpexLimit)}}
                            </th>


                        </tr>
                        <tr v-show="budget.OpexCollapsed === false" v-for="p in budget.OpexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOpexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOpexLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="capex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.CapexCollapsed = !budget.CapexCollapsed">
                                    <i :class="['fa', budget.CapexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Capex
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.CapexCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.CapexLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.CapexCollapsed === false" v-for="p in budget.CapexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>

                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showCapexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showCapexLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="othex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OthexCollapsed = !budget.OthexCollapsed">
                                    <i :class="['fa', budget.OthexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Other<br />
                                <small class="text-muted">Non-Opex, Non-COGS</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OthexCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OthexLimit)}}
                            </th>


                        </tr>
                        <tr v-show="budget.OthexCollapsed === false" v-for="p in budget.OthexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOthexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOthexLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                    <tbody class="expenses">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.ExpensesCollapsed = !budget.ExpensesCollapsed">
                                    <i :class="['fa', budget.ExpensesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Expenses (All)<br />
                                <small class="text-muted">Cogs, Opex, Other</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.ExpensesCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.ExpensesLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.ExpensesCollapsed === false" v-for="p in budget.ExpensesItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showExpensesLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showExpensesLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                    <tbody class="opex-expenses">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OpexExpensesCollapsed = !budget.OpexExpensesCollapsed">
                                    <i :class="['fa', budget.OpexExpensesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Expenses (Opex)<br />
                                <small class="text-muted">Opex Expense Accounts</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OpexExpensesCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OpexExpensesLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.OpexExpensesCollapsed === false" v-for="p in budget.OpexExpensesItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOpexExpensesLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOpexExpensesLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                </table>

            </div>

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th colspan="2" class="align-middle">Resource allocation</th>
                            <th class="align-middle text-center">
                                Target
                                <br />
                                <small class="text-muted">% of available</small>
                            </th>
                            <th class="align-middle text-center">
                                Capacity <br />
                                <small class="text-muted">Target based</small>
                            </th>
                            <th class="align-middle text-center">Allocation</th>
                        </tr>
                    </thead>
                    <tbody v-for="r in resources" :key="r.Id">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="r.IsCollapsed = !r.IsCollapsed">
                                    <i :class="['fa', r.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                {{r.Name}}
                            </th>
                            <th class="text-center">
                                <span :class="[r.TargetPercent > 1  ? 'text-danger' : 'text-dark']">{{fmtPct(r.TargetPercent, 0)}}</span>
                            </th>

                            <th class="text-center">
                                <span v-if="r.TargetValue != null" :class="[r.TargetPercent > 1  ? 'text-danger' : 'text-dark']">{{fmtNum(r.TargetValue)}} {{r.Unit}}</span>
                            </th>

                            <th class="text-center">
                                {{fmtNum(r.CurrentValue)}} {{r.Unit}} <br />
                                <small class="text-muted">{{fmtPct(r.CurrentPercent)}}</small>
                            </th>
                        </tr>
                        <tr v-show="r.IsCollapsed == false" v-for="p in r.Portfolios" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>

                            <td class="text-center">
                                <template v-if="p.IsLinked === true">
                                    <template v-if="p.TargetPercent != null">
                                        <a class="text-primary" @click="showPortfolioResourceAllocationEditorDialog(p)">{{fmtPct(p.TargetPercent, 0)}}</a>
                                    </template>
                                    <template v-if="p.TargetPercent == null">
                                        <a class="text-muted font-italic pointer" @click="showPortfolioResourceAllocationEditorDialog(p)">Not set</a>
                                    </template>
                                </template>
                            </td>
                            <td class="text-center">
                                <template v-if="p.IsLinked === true && p.TargetValue != null">
                                    {{fmtNum(p.TargetValue)}} {{r.Unit}}
                                </template>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}<br />
                                <small class="text-muted">{{fmtPct(p.CurrentPercent)}}</small>
                            </td>
                        </tr>

                    </tbody>


                </table>
            </div>


            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th>Capacity minus allocated</th>
                            <th class="text-center">Jan</th>
                            <th class="text-center">Feb</th>
                            <th class="text-center">Mar</th>
                            <th class="text-center">Apr</th>
                            <th class="text-center">May</th>
                            <th class="text-center">Jun</th>
                            <th class="text-center">Jul</th>
                            <th class="text-center">Aug</th>
                            <th class="text-center">Sep</th>
                            <th class="text-center">Oct</th>
                            <th class="text-center">Nov</th>
                            <th class="text-center">Dec</th>
                        </tr>
                    </thead>
                    <tbody v-for="r in rm.Usage" :key="r.Id">
                        <tr>
                            <td>
                                <a class="text-primary" v-sanitizedHref="`/#/enterprise/resource-capacity/${workspaceId}/${strategyId}/${r.Id}`">{{r.Name}} {{r.Unit}}</a>
                            </td>
                            <td colspan="12"></td>
                        </tr>
                        <tr v-for="y in r.Years" :key="y.Year">
                            <td class="align-middle text-center">{{y.Year}}</td>
                            <td class="align-middle text-center" v-for="i in 12" :key="i" :class="{'table-danger': y.Capacity[i-1] - y.Allocation[i-1] < 0}">
                                {{fmtNum(y.Capacity[i-1] - y.Allocation[i-1])}}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
